import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import SellerLayout from "@/components/layouts/SellerLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Check, Crown, Clock } from "lucide-react";
import { apiError } from "@/lib/apiError";

const PlanCard = ({ plan, isCurrent, isPending, disabled, onRequest }) => (
  <Card
    className={`border-2 shadow-sm relative transition-transform duration-200 hover:-translate-y-1 ${
      isCurrent ? "border-primary-500" : plan.id === "growth" ? "border-secondary-300" : "border-gray-100"
    }`}
    data-testid={`plan-card-${plan.id}`}
  >
    <CardContent className="p-6 flex flex-col h-full">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-heading text-xl font-bold text-gray-900">{plan.name}</h3>
        {isCurrent && <Badge className="bg-primary-100 text-primary-700" data-testid={`plan-current-badge-${plan.id}`}>Current</Badge>}
        {isPending && <Badge className="bg-yellow-100 text-yellow-700"><Clock className="w-3 h-3 mr-1" />Requested</Badge>}
      </div>
      <p className="mb-1">
        <span className="text-3xl font-bold text-gray-900">{plan.price ? `₹${plan.price}` : "Free"}</span>
        {plan.price > 0 && <span className="text-gray-500 text-sm"> / {plan.period}</span>}
      </p>
      <p className="text-sm text-gray-500 mb-5">
        {plan.product_limit ? `Up to ${plan.product_limit} products` : "Unlimited products"}
      </p>
      <ul className="space-y-2 mb-6 flex-1">
        {plan.highlights.map((h) => (
          <li key={h} className="flex items-start gap-2 text-sm text-gray-700">
            <Check className="w-4 h-4 text-primary-500 mt-0.5 flex-shrink-0" />
            {h}
          </li>
        ))}
      </ul>
      <Button
        className="w-full rounded-full bg-secondary-500 !text-primary-900 hover:bg-secondary-400 disabled:bg-primary-100 disabled:!text-primary-700 disabled:opacity-100"
        disabled={isCurrent || isPending || disabled}
        onClick={() => onRequest(plan.id)}
        data-testid={`plan-request-btn-${plan.id}`}
      >
        {isCurrent ? "Active Plan" : isPending ? "Awaiting Admin" : "Choose Plan"}
      </Button>
    </CardContent>
  </Card>
);

const SellerPlans = () => {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/seller/plans`, { headers: getAuthHeaders() });
      setData(res.data);
    } catch (err) {
      toast.error(apiError(err, "Failed to load plans"));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requestPlan = async (planId) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/seller/plans/request`, { plan_id: planId }, { headers: getAuthHeaders() });
      toast.success(res.data.message);
      await load();
    } catch (err) {
      toast.error(apiError(err, "Failed to request plan"));
    }
    setBusy(false);
  };

  const current = data?.plans.find((p) => p.id === data.current_plan);

  return (
    <SellerLayout title="Plans">
      {!data ? (
        <div className="p-12 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" /></div>
      ) : (
        <div className="space-y-6" data-testid="seller-plans-page">
          <Card className="border-0 shadow-sm bg-gradient-to-r from-primary-50 to-white">
            <CardContent className="p-5 flex flex-col md:flex-row md:items-center gap-4">
              <Crown className="w-10 h-10 text-secondary-500" />
              <div className="flex-1">
                <p className="text-sm text-gray-500">Your current plan</p>
                <p className="font-heading text-xl font-bold text-gray-900" data-testid="current-plan-name">{current?.name}</p>
                <p className="text-sm text-gray-600" data-testid="plan-product-usage">
                  {data.product_count} / {current?.product_limit ?? "∞"} products used
                </p>
              </div>
              {data.pending_request && (
                <Badge className="bg-yellow-100 text-yellow-700 self-start md:self-center" data-testid="pending-plan-request">
                  Upgrade to {data.plans.find((p) => p.id === data.pending_request.plan_id)?.name} awaiting admin approval
                </Badge>
              )}
            </CardContent>
          </Card>
          <div className="grid md:grid-cols-3 gap-6">
            {data.plans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                isCurrent={plan.id === data.current_plan}
                isPending={data.pending_request?.plan_id === plan.id}
                disabled={busy}
                onRequest={requestPlan}
              />
            ))}
          </div>
          <p className="text-sm text-gray-500">
            Choosing a plan sends a request to the M Ventures team. Your plan activates as soon as the admin approves it.
          </p>
        </div>
      )}
    </SellerLayout>
  );
};

export default SellerPlans;
